import type {UseQueryResult} from '@tanstack/react-query'
import chunk from 'lodash/chunk'
import omit from 'lodash/omit'
import {exhaustive} from '@freckle/exhaustive'
import {useAsync} from '@freckle/educator-entities/ts/common/hooks/use-async'
import type {FancyOk, fancyOperations} from '@freckle/educator-entities/ts/openapi-clients'
import {fancyClient, extractData} from '@freckle/educator-entities/ts/openapi-clients'
import type {ManagedCountT} from '@freckle/educator-entities/ts/roster/identity-provider'
import {combineManagedCounts} from '@freckle/educator-entities/ts/roster/identity-provider'
import type {DashboardIdentityProviderT} from '@freckle/school/ts/common/helpers/api-helper'

export type RosterSyncEventT = FancyOk<fancyOperations['getSchoolRosterSyncEventsLatestR']>

export type RosterSyncedByT = RosterSyncEventT['syncedBy']

// Only fetch if exactly 1 school ID is given
const fetchLatestRosterSyncEvent = (schoolIds: number[]): Promise<RosterSyncEventT | null> => {
  const firstSchoolId = schoolIds[0]
  if (firstSchoolId === undefined || schoolIds.length > 1) {
    return Promise.resolve(null)
  }

  return fancyClient
    .GET('/2/schools/{SchoolP-SchoolId}/roster-sync-events/latest', {
      params: {path: {'SchoolP-SchoolId': firstSchoolId}},
    })
    .then(extractData)
    .then(
      v =>
        // I think the spec's wrong here... This endpoint returns a `Maybe`.
        // Ensure it becomes `null` if not defined.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        v ?? null,
    )
}

export type SourceCountT = FancyOk<fancyOperations['getSchoolAdminCountsStudentsR']>

export type CourseSourceCountT = FancyOk<fancyOperations['getSchoolAdminCountsCoursesR']>

export type SummaryT = {
  teacherCounts: SourceCountT
  studentCounts: SourceCountT
  courseCounts: CourseSourceCountT
  mostRecentRosterSyncEvent: RosterSyncEventT | null
  teachersMissingEmailCount: number
}

const sourceCountsManagedCount = (sourceCount: SourceCountT): ManagedCountT =>
  omit(sourceCount, ['idpSync', 'manual', 'unknown'])

const courseCountsManagedCount = (courseCounts: CourseSourceCountT): ManagedCountT =>
  omit(courseCounts, ['nonSis', 'sis', 'teacher'])

const combineSourceCounts = (a: SourceCountT, b: SourceCountT): SourceCountT => ({
  ...combineManagedCounts(sourceCountsManagedCount(a), sourceCountsManagedCount(b)),
  idpSync: a.idpSync + b.idpSync,
  manual: a.manual + b.manual,
  unknown: a.unknown + b.unknown,
})

const combineCourseCounts = (a: CourseSourceCountT, b: CourseSourceCountT): CourseSourceCountT => ({
  ...combineManagedCounts(courseCountsManagedCount(a), courseCountsManagedCount(b)),
  nonSis: a.nonSis + b.nonSis,
  sis: a.sis + b.sis,
  teacher: a.teacher + b.teacher,
})

export const combineSummaries = (a: SummaryT, b: SummaryT): SummaryT => ({
  // We'll add this in later, if we can find it
  mostRecentRosterSyncEvent: null,
  teacherCounts: combineSourceCounts(a.teacherCounts, b.teacherCounts),
  studentCounts: combineSourceCounts(a.studentCounts, b.studentCounts),
  courseCounts: combineCourseCounts(a.courseCounts, b.courseCounts),
  teachersMissingEmailCount: a.teachersMissingEmailCount + b.teachersMissingEmailCount,
})

export const getStudentCountForIdp = (
  studentCounts: SummaryT['studentCounts'],
  idpProvider: DashboardIdentityProviderT,
): number => studentCounts[toCountKey(idpProvider)]

function toCountKey(
  idpProvider: DashboardIdentityProviderT,
): 'manual' | 'clever' | 'classlink' | 'upload' | 'renaissance' {
  switch (idpProvider) {
    case 'manual':
      return 'manual'
    case 'clever':
      return 'clever'
    case 'class-link':
      return 'classlink'
    case 'csv':
      return 'upload'
    case 'renaissance':
      return 'renaissance'
    default:
      return exhaustive(idpProvider)
  }
}

const fetchStudentCounts = (
  adminId: number,
  schoolIds: Array<number>,
): Promise<SummaryT['studentCounts']> => {
  const params = {path: {'SchoolAdminP-SchoolAdminId': adminId}, query: {schoolIds}}

  return fancyClient
    .GET('/2/school-admins/{SchoolAdminP-SchoolAdminId}/counts/students', {params})
    .then(extractData)
}

const fetch = async (adminId: number, schoolIds: Array<number>): Promise<SummaryT> => {
  const params = {path: {'SchoolAdminP-SchoolAdminId': adminId}, query: {schoolIds}}
  const [teacherCounts, studentCounts, courseCounts, teachersMissingEmailCount] = await Promise.all(
    [
      fancyClient
        .GET('/2/school-admins/{SchoolAdminP-SchoolAdminId}/counts/teachers', {params})
        .then(extractData),
      fetchStudentCounts(adminId, schoolIds),
      fancyClient
        .GET('/2/school-admins/{SchoolAdminP-SchoolAdminId}/counts/courses', {params})
        .then(extractData),
      fancyClient
        .GET('/2/school-admins/{SchoolAdminP-SchoolAdminId}/counts/teachers-missing-email', {
          params,
        })
        .then(extractData),
    ],
  )

  return {
    teacherCounts,
    studentCounts,
    courseCounts,
    teachersMissingEmailCount,
    mostRecentRosterSyncEvent: null,
  }
}

const SUMMARY_BATCH_SIZE = 50

export const useStudentCounts = (
  adminId: number,
  schoolIds: Array<number>,
): UseQueryResult<SourceCountT> =>
  useAsync(
    () =>
      schoolIds.length === 0
        ? Promise.resolve({
            classlink: 0,
            clever: 0,
            idpSync: 0,
            manual: 0,
            renaissance: 0,
            unknown: 0,
            upload: 0,
          })
        : Promise.all(
            chunk(schoolIds, SUMMARY_BATCH_SIZE).map(schoolIdsChunk =>
              fetchStudentCounts(adminId, schoolIdsChunk),
            ),
          ).then(counts => counts.reduce(combineSourceCounts)),
    'useStudentCounts',
    [adminId, schoolIds],
  )

export async function fetchSummary(adminId: number, schoolIds: Array<number>) {
  const tasks = chunk(schoolIds, SUMMARY_BATCH_SIZE).map(schoolIdsChunk =>
    fetch(adminId, schoolIdsChunk),
  )
  const [mostRecentRosterSyncEvent, resolvedTasks] = await Promise.all([
    fetchLatestRosterSyncEvent(schoolIds),
    Promise.all(tasks),
  ])
  const result = resolvedTasks.reduce(combineSummaries) as SummaryT
  return {...result, mostRecentRosterSyncEvent}
}
